import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate"
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Skeleton from "@mui/material/Skeleton"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import { styled } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../common/components/PageTopSection"
import { RowButColumnOnMobile } from "../../../common/components/Row"
import NoResultsTableRow from "../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import {
  HeaderTableCell,
  NoWrapTableCell,
  SortableHeaderCell,
  StickyHeaderTableCell,
} from "../../../common/components/tables/cells"
import {
  getTableSearchParams,
  usePaginationState,
  useTableSearchTextState,
  useTableSort,
} from "../../../common/components/tables/utils"
import {
  formatDateTime,
  formatPhone,
  trimLongString,
} from "../../../common/utils"
import BorderedGrayChip from "../components/BorderedGrayChip"
import {
  CandidateActionDialogs,
  useCandidateActions,
} from "../components/CandidateActionController"
import { phoneDisplay } from "../components/NameWithContactActionsCell"
import PageContentContainer from "../components/PageContentContainer"
import SectionSeparator from "../components/SectionSeparator"
import SelectMultipleTableCell, {
  useSelectMultipleTableCellState,
} from "../components/SelectMultipleTableCell"
import TaskDialog, {
  TASK_ACTIONS,
  useTaskDialogState,
} from "../components/TaskDialog"
import { isCheckboxClickEvent, isTaskOverdue } from "../utils"
import ConfirmDeleteTasksDialog from "./ConfirmDeleteTasksDialog"
import TaskWorkDrawer from "./TaskWorkDrawer"
import { TASK_TYPES } from "./utils"

export default function Tasks() {
  const {
    tasks,
    setTasks,
    clickedTask,
    setClickedTask,
    taskDialogOpen,
    openTaskDialog,
    handleTaskDialogClosed,
    isSnackbarOpen,
    openSnackbar,
    closeSnackbar,
    snackbarMessage,
  } = useTaskDialogState()
  const [workTask, setWorkTask] = useState(null)
  const [workDrawerOpen, setWorkDrawerOpen] = useState(false)
  const [followUpCandidate, setFollowUpCandidate] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const {
    pageIndex,
    setPageIndex,
    totalResultsSize,
    setTotalResultsSize,
    pageSize,
    setPageSize,
  } = usePaginationState()
  const [searchText, setSearchText, debouncedSearchText] =
    useTableSearchTextState("q", setPageIndex)
  const { sortColumn, sortDirection, onSortColumnChanged, serverSort } =
    useTableSort("created", "desc")

  const [selectedTasksAction, setSelectedTasksAction] = useState(
    SELECTED_TASK_ACTIONS.markComplete
  )
  const [actionMenuAnchorElem, setActionMenuAnchorElem] = useState(null)
  const [confirmDeleteTasksDialogOpen, setConfirmDeleteTasksDialogOpen] =
    useState(false)
  const { checkedIDS, setCheckedIDS, checkedIDsArray, clearSelection } =
    useSelectMultipleTableCellState()
  const [searchParams, setSearchParams] = useSearchParams()
  const axios = useAuthorizedAxiosClient()
  const candidateActions = useCandidateActions(openSnackbar)
  const openedTaskIdRef = useRef(null)

  function handleRowClicked(e, task) {
    if (isCheckboxClickEvent(e)) {
      return
    }
    setWorkTask(task)
    setWorkDrawerOpen(true)
  }

  function handleWorkDrawerClosed() {
    setWorkDrawerOpen(false)
    setWorkTask(null)
  }

  function handleWorkTaskUpdated(updatedTask) {
    setTasks((prev) =>
      prev.map((task) => (task.id === updatedTask.id ? updatedTask : task))
    )
    setWorkTask(updatedTask)
  }

  function handleEditTaskFromDrawer(task) {
    setFollowUpCandidate(null)
    setClickedTask(task)
    openTaskDialog()
  }

  function handleScheduleNewTask(task) {
    setClickedTask(null)
    setFollowUpCandidate(task?.candidate || null)
    openTaskDialog()
  }

  function handleTaskDialogClosedWrapper(newOrUpdatedTask, taskAction) {
    if (newOrUpdatedTask) {
      if (clickedTask) {
        if (taskAction === TASK_ACTIONS.edited) {
          setTasks((prev) =>
            prev.map((task) =>
              task.id === clickedTask.id ? newOrUpdatedTask : task
            )
          )
          if (workTask?.id === clickedTask.id) {
            setWorkTask(newOrUpdatedTask)
          }
          openSnackbar("Task updated")
        } else if (taskAction === TASK_ACTIONS.deleted) {
          setTasks((prev) => prev.filter((task) => task.id !== clickedTask.id))
          if (workTask?.id === clickedTask.id) {
            handleWorkDrawerClosed()
          }
          openSnackbar("Task deleted")
        }
      } else {
        // refetch so the list, page size and total count stay in sync
        setRefreshKey((key) => key + 1)
        openSnackbar("Task created successfully")
        if (workDrawerOpen) {
          setWorkTask(newOrUpdatedTask)
        }
      }
    }
    setFollowUpCandidate(null)
    handleTaskDialogClosed(null)
  }

  function handleTasksActionButtonClicked() {
    if (selectedTasksAction === SELECTED_TASK_ACTIONS.markComplete) {
      setIsLoading(true)
      axios
        .post("/crm/api/tasks/bulk/mark-complete/", {
          task_ids: checkedIDsArray,
        })
        .then(() => {
          setTasks(
            tasks.map((task) =>
              checkedIDS.has(task.id) ? { ...task, completed: true } : task
            )
          )
          if (workTask && checkedIDS.has(workTask.id)) {
            setWorkTask({ ...workTask, completed: true })
          }
          openSnackbar(`${checkedIDS.size} tasks marked as complete`)
          clearSelection()
        })
        .finally(() => setIsLoading(false))
    } else {
      setConfirmDeleteTasksDialogOpen(true)
    }
  }

  function handleConfirmDeleteTasksDialogClosed(confirmed) {
    if (confirmed) {
      setIsLoading(true)
      axios
        .delete("/crm/api/tasks/bulk/delete/", {
          data: { task_ids: checkedIDsArray },
        })
        .then(() => {
          setTasks(tasks.filter((task) => !checkedIDS.has(task.id)))
          if (workTask && checkedIDS.has(workTask.id)) {
            handleWorkDrawerClosed()
          }
          openSnackbar(`${checkedIDS.size} tasks deleted`)
          clearSelection()
        })
        .finally(() => setIsLoading(false))
    }
    setConfirmDeleteTasksDialogOpen(false)
  }

  function handleActionMenuItemClicked(selectedAction) {
    setSelectedTasksAction(selectedAction)
    closeActionMenu()
  }

  function closeActionMenu() {
    setActionMenuAnchorElem(null)
  }

  function handleAddTaskClicked() {
    setFollowUpCandidate(null)
    setClickedTask(null)
    openTaskDialog()
  }

  useEffect(
    () => {
      setIsLoading(true)
      const dueDateFilter = searchParams.get("due_date")
      if (dueDateFilter) {
        setSearchParams({ ...searchParams, due_date: "" })
      }
      axios
        .get("/crm/api/tasks/", {
          params: {
            ...getTableSearchParams(
              debouncedSearchText,
              pageIndex,
              pageSize,
              serverSort
            ),
            due_date: dueDateFilter,
          },
        })
        .then(({ data }) => {
          setTasks(data.results)
          setTotalResultsSize(data.count)
        })
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchText, serverSort, pageIndex, pageSize, refreshKey]
  )

  useEffect(() => {
    const taskId = searchParams.get("id")
    if (!taskId || openedTaskIdRef.current === taskId) {
      return
    }
    const existing = tasks.find((task) => String(task.id) === String(taskId))
    if (existing) {
      openedTaskIdRef.current = taskId
      setWorkTask(existing)
      setWorkDrawerOpen(true)
      return
    }
    openedTaskIdRef.current = taskId
    axios
      .get(`/crm/api/tasks/${taskId}/`)
      .then(({ data }) => {
        setWorkTask(data)
        setWorkDrawerOpen(true)
      })
      .catch(() => {
        openedTaskIdRef.current = null
      })
    // Drawer openers are recreated each render; id + loaded rows are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axios, searchParams, tasks])

  return (
    <Column>
      <PageTopSection title="Tasks" />
      <PageContentContainer>
        <RowButColumnOnMobile
          sx={{ justifyContent: { xs: "stretch", sm: "flex-end" }, gap: 2 }}
        >
          <ButtonGroup
            variant="outlined"
            size="small"
            disabled={checkedIDS.size === 0}
          >
            <Button
              onClick={handleTasksActionButtonClicked}
              color={
                selectedTasksAction === SELECTED_TASK_ACTIONS.markComplete
                  ? "primary"
                  : "error"
              }
              sx={{ width: { xs: "100%", sm: "140px" } }}
            >
              {selectedTasksAction === SELECTED_TASK_ACTIONS.markComplete
                ? "Mark complete"
                : "Delete"}
            </Button>
            <Button onClick={(e) => setActionMenuAnchorElem(e.currentTarget)}>
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Menu
            open={!!actionMenuAnchorElem}
            anchorEl={actionMenuAnchorElem}
            onClose={closeActionMenu}
          >
            <MenuItem
              onClick={() =>
                handleActionMenuItemClicked(SELECTED_TASK_ACTIONS.markComplete)
              }
            >
              Mark complete
            </MenuItem>
            <MenuItem
              onClick={() =>
                handleActionMenuItemClicked(SELECTED_TASK_ACTIONS.delete)
              }
            >
              Delete
            </MenuItem>
          </Menu>
          <ConfirmDeleteTasksDialog
            open={confirmDeleteTasksDialogOpen}
            onClose={handleConfirmDeleteTasksDialogClosed}
            taskCount={checkedIDS.size}
          />
          <TextField
            label="Search tasks"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: "220px" }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlinedIcon />}
            onClick={handleAddTaskClicked}
          >
            Add task
          </Button>
          <TaskDialog
            open={taskDialogOpen}
            onClose={handleTaskDialogClosedWrapper}
            task={clickedTask}
            candidate={followUpCandidate}
          />
          <TaskWorkDrawer
            open={workDrawerOpen}
            onClose={handleWorkDrawerClosed}
            task={workTask}
            tasks={tasks}
            onTaskUpdated={handleWorkTaskUpdated}
            onEditTask={handleEditTaskFromDrawer}
            onScheduleNewTask={handleScheduleNewTask}
            onSelectTask={setWorkTask}
            onCall={candidateActions.handleCallButtonClicked}
            onSMS={candidateActions.handleSMSButtonClicked}
            onEmail={candidateActions.handleEmailButtonClicked}
            onSnackbar={openSnackbar}
          />
        </RowButColumnOnMobile>
        <SectionSeparator>
          <ConditionalPurpleLinearProgress visible={isLoading} />
          <ScrollableTableContainer
            sx={{ maxHeight: { xs: "calc(100vh - 410px)" } }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <HeaderTableCell sx={{ width: "40px" }} />
                  <SortableHeaderCell
                    columnName="completed"
                    label="Status"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onClick={onSortColumnChanged}
                    props={{ sx: { width: "80px" } }}
                  />
                  <SortableHeaderCell
                    HeaderCellComponent={StickyHeaderTableCell}
                    columnName="title"
                    label="Title"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onClick={onSortColumnChanged}
                  />
                  <SortableHeaderCell
                    columnName="candidate__candidate_name"
                    label="Candidate"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onClick={onSortColumnChanged}
                  />
                  <HeaderTableCell>Phone</HeaderTableCell>
                  <HeaderTableCell>Brokerage</HeaderTableCell>
                  <SortableHeaderCell
                    columnName="type"
                    label="Type"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onClick={onSortColumnChanged}
                  />
                  <SortableHeaderCell
                    columnName="priority"
                    label="Priority"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onClick={onSortColumnChanged}
                  />
                  <SortableHeaderCell
                    columnName="due_datetime"
                    label="Due date"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onClick={onSortColumnChanged}
                  />
                  <SortableHeaderCell
                    columnName="created"
                    label="Created"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onClick={onSortColumnChanged}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRow key={index}>
                      <NoWrapTableCell>
                        <Skeleton variant="rounded" width={24} height={24} />
                      </NoWrapTableCell>
                      <NoWrapTableCell sx={{ textAlign: "center" }}>
                        <Skeleton
                          variant="circular"
                          width={32}
                          height={32}
                          sx={{ mx: "auto" }}
                        />
                      </NoWrapTableCell>
                      <NoWrapTableCell>
                        <Skeleton variant="text" width={200} />
                      </NoWrapTableCell>
                      <NoWrapTableCell>
                        <Skeleton variant="text" width={150} />
                      </NoWrapTableCell>
                      <NoWrapTableCell>
                        <Skeleton variant="text" width={110} />
                      </NoWrapTableCell>
                      <NoWrapTableCell>
                        <Skeleton variant="text" width={120} />
                      </NoWrapTableCell>
                      <NoWrapTableCell>
                        <Skeleton variant="rounded" width={80} height={24} />
                      </NoWrapTableCell>
                      <NoWrapTableCell>
                        <Skeleton variant="rounded" width={60} height={24} />
                      </NoWrapTableCell>
                      <NoWrapTableCell>
                        <Skeleton variant="text" width={150} />
                      </NoWrapTableCell>
                      <NoWrapTableCell>
                        <Skeleton variant="text" width={150} />
                      </NoWrapTableCell>
                    </TableRow>
                  ))
                ) : tasks.length === 0 ? (
                  <NoResultsTableRow colSpan={10} />
                ) : (
                  tasks.map((task, i) => {
                    const candidatePhone = phoneDisplay(task.candidate?.phone)
                    const candidateEmail = task.candidate?.email?.trim()
                    const isActiveWorkTask =
                      workDrawerOpen && workTask?.id === task.id
                    return (
                      <TableRow
                        key={task.id}
                        selected={isActiveWorkTask}
                        sx={{
                          cursor: "pointer",
                          bgcolor: isActiveWorkTask
                            ? "action.selected"
                            : undefined,
                        }}
                        onClick={(e) => handleRowClicked(e, task)}
                      >
                        <SelectMultipleTableCell
                          checkedIDs={checkedIDS}
                          setCheckedIDS={setCheckedIDS}
                          index={i}
                          row={task}
                          rows={tasks}
                        />
                        <NoWrapTableCell sx={{ textAlign: "center" }}>
                          {isTaskOverdue(task) ? (
                            <StyledAssignmentLateIcon />
                          ) : (
                            <StyledAssignmentTurnedInIcon
                              completed={task.completed}
                            />
                          )}
                        </NoWrapTableCell>
                        <NoWrapTableCell title={task.title}>
                          {trimLongString(task.title)}
                        </NoWrapTableCell>
                        <NoWrapTableCell
                          title={task.candidate.candidate_name}
                          sx={{ minWidth: 200 }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <span>
                              {trimLongString(task.candidate.candidate_name)}
                            </span>
                            <ButtonGroup
                              variant="outlined"
                              size="small"
                              sx={{ flexShrink: 0 }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <Tooltip
                                title={
                                  candidatePhone ? "Call" : "No phone number"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!candidatePhone}
                                    onClick={(e) =>
                                      candidateActions.handleCallButtonClicked(
                                        e,
                                        task.candidate
                                      )
                                    }
                                  >
                                    <PhoneOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title={candidateEmail ? "Email" : "No email"}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!candidateEmail}
                                    onClick={(e) =>
                                      candidateActions.handleEmailButtonClicked(
                                        e,
                                        task.candidate
                                      )
                                    }
                                  >
                                    <EmailOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </ButtonGroup>
                          </Stack>
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          {candidatePhone
                            ? formatPhone(task.candidate.phone)
                            : "—"}
                        </NoWrapTableCell>
                        <NoWrapTableCell
                          title={task.candidate?.company_name || ""}
                        >
                          {trimLongString(
                            task.candidate?.company_name || "—",
                            30
                          )}
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <BorderedGrayChip label={TASK_TYPES[task.type]} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <BorderedGrayChip label={task.priority} />
                        </NoWrapTableCell>
                        <NoWrapTableCell
                          sx={{
                            color: isTaskOverdue(task)
                              ? "error.main"
                              : "inherit",
                          }}
                        >
                          {formatDateTime(task.due_datetime)}
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          {formatDateTime(task.created)}
                        </NoWrapTableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
          <TablePaginationRow
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            pageSize={pageSize}
            setPageSize={setPageSize}
            tableRows={tasks}
            totalResultsSize={totalResultsSize}
          />
        </SectionSeparator>
      </PageContentContainer>
      <CloseableSnackbar
        message={snackbarMessage}
        onClose={closeSnackbar}
        open={isSnackbarOpen}
      />
      <CandidateActionDialogs actionsState={candidateActions} />
    </Column>
  )
}

const COMMON_ICON_STYLE = {
  fontSize: "1.8em",
}

const StyledAssignmentTurnedInIcon = styled(AssignmentTurnedInIcon, {
  shouldForwardProp: (prop) => prop !== "completed",
})(({ theme, completed }) => ({
  fill: completed ? theme.palette.primary.main : null,
  ...COMMON_ICON_STYLE,
}))

const StyledAssignmentLateIcon = styled(AssignmentLateIcon)(({ theme }) => ({
  fill: theme.palette.error.main,
  ...COMMON_ICON_STYLE,
}))

const SELECTED_TASK_ACTIONS = {
  markComplete: 1,
  delete: 2,
}
